import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Settings2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Switch } from './ui/switch';
import { DropPushStats } from './DropPushStats';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const opts = { withCredentials: true };
const inputCls = 'bg-[#050003] border border-[#2A0F3D] focus:border-[#FF9F0A] font-mono text-sm px-2 py-1 text-white outline-none w-16 text-center';

function Num({ value, onChange, testId, min = 0 }) {
  return <input type="number" min={min} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testId} />;
}

function RegionSettings({ region }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setS(null);
    axios.get(`${API_URL}/api/drop/settings/${region}`, opts).then(({ data }) => setS(data)).catch(() => toast.error('Could not load Drop settings'));
  }, [region]);
  if (!s) return <p className="font-mono text-xs text-[#6B8E73]">Loading…</p>;
  const set = (k) => (v) => setS({ ...s, [k]: v });
  const save = async () => {
    setSaving(true);
    try {
      const body = { expire_days: +s.expire_days, push_enabled: s.push_enabled, push_max: +s.push_max, push_window_days: +s.push_window_days, push_min_gap_hours: +s.push_min_gap_hours };
      setS((await axios.patch(`${API_URL}/api/drop/settings/${region}`, body, opts)).data);
      window.dispatchEvent(new CustomEvent('drop-settings-saved', { detail: region }));
      toast.success(`Drop settings saved · ${region.toUpperCase()}`);
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };
  return (
    <div className="space-y-4 font-mono text-xs text-[#A3C7A8]" data-testid={`drop-settings-${region}`}>
      <div className="space-y-1">
        <p className="text-[#FF9F0A] uppercase text-[11px]">Auto-expire</p>
        <p className="flex items-center gap-2 flex-wrap">Drop items turn off after <Num value={s.expire_days} onChange={set('expire_days')} testId="drop-expire-days" /> days <span className="text-[#6B8E73]">(0 = never)</span></p>
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-[#FF9F0A] uppercase text-[11px]">Push customers when a product joins The Drop</p>
          <Switch checked={s.push_enabled} onCheckedChange={set('push_enabled')} data-testid="drop-push-enabled" />
        </div>
        <p className="flex items-center gap-2 flex-wrap">At most <Num min={1} value={s.push_max} onChange={set('push_max')} testId="drop-push-max" /> pushes every <Num min={1} value={s.push_window_days} onChange={set('push_window_days')} testId="drop-push-window" /> day(s)</p>
        <p className="flex items-center gap-2 flex-wrap">At least <Num value={s.push_min_gap_hours} onChange={set('push_min_gap_hours')} testId="drop-push-gap" /> hours between pushes</p>
      </div>
      <button type="button" onClick={save} disabled={saving} className="w-full h-10 border border-[#FF9F0A] text-[#FF9F0A] hover:bg-[#FF9F0A]/10 uppercase tracking-wider disabled:opacity-50" data-testid="drop-settings-save">{saving ? 'Saving…' : 'Save'}</button>
    </div>
  );
}

export function DropSettingsDialog({ regions }) {
  const [region, setRegion] = useState(regions[0]);
  const [tab, setTab] = useState('settings');
  useEffect(() => { if (!regions.includes(region)) setRegion(regions[0]); }, [regions, region]);
  if (!regions.length) return null;
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button type="button" className="inline-flex items-center gap-1.5 px-3 h-9 border border-[#FF9F0A] text-[#FF9F0A] font-mono text-xs uppercase hover:bg-[#FF9F0A]/10" data-testid="drop-settings-btn">
          <Settings2 className="w-3.5 h-3.5" /> Drop settings
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#0C0612] border-[#FF9F0A]/40 max-w-md max-h-[90vh] overflow-y-auto" data-testid="drop-settings-dialog">
        <DialogHeader><DialogTitle className="font-mono text-[#FF9F0A]">⭐ THE DROP SETTINGS</DialogTitle></DialogHeader>
        {regions.length > 1 && (
          <div className="flex gap-2">
            {regions.map((r) => (
              <button key={r} type="button" onClick={() => setRegion(r)} className={`px-3 py-1 font-mono text-xs uppercase border ${r === region ? 'border-[#FF9F0A] text-[#FF9F0A]' : 'border-[#2A0F3D] text-[#6B8E73]'}`} data-testid={`drop-settings-region-${r}`}>{r}</button>
            ))}
          </div>
        )}
        <div className="flex border-b border-[#2A0F3D]">
          {[['settings', 'Settings'], ['stats', 'Push stats']].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setTab(k)} className={`px-3 py-1.5 font-mono text-xs uppercase -mb-px border-b-2 ${tab === k ? 'border-[#FF9F0A] text-[#FF9F0A]' : 'border-transparent text-[#6B8E73]'}`} data-testid={`drop-tab-${k}`}>{label}</button>
          ))}
        </div>
        {region && (tab === 'settings' ? <RegionSettings key={region} region={region} /> : <DropPushStats key={region} region={region} />)}
      </DialogContent>
    </Dialog>
  );
}
